<template>
  <div
    v-if="eventData"
    class="w-full lg:w-[420px] flex flex-col gap-4 mt-5 kft-product__checkout"
  >
    <!-- Buy box + giftcard card. This is the sticky-bar trigger target -->
    <!-- (team-event / corporate-gifting cards below are intentionally excluded). -->
    <div ref="buyBoxContainer" class="flex flex-col gap-4">
      <!-- Placeholder to prevent CLS -->
      <div
        v-show="!isBuyBoxReady"
        class="preloader loading-effect"
        :class="isSpecialGiftcardEvent ? 'min-h-[190px]' : 'min-h-[300px]'"
      ></div>

      <EventBuyBoxNoDates
        v-if="hasNoDates"
        :show-giftcard="!!shouldEnableGiftcard"
        :show-date-request="!isKitsPage && !isEmbedded"
        :show-waitlist="!isEmbedded"
        :is-embedded="isEmbedded"
        :is-kits-page="isKitsPage"
        :background-color="backgroundColor"
        :text-color="textColor"
        @add-item-to-cart="emit('add-item-to-cart')"
      />

      <!-- EventBuyBox with v-if to only render when dates are available -->
      <EventBuyBox
        v-if="isBuyBoxShown"
        :is-embedded="isEmbedded"
        :text-color="textColor"
        :background-color="backgroundColor"
        :card-background-color="cardBackgroundColor"
        :is-kits-page="isKitsPage"
        :enable-giftcard="false"
        :available-dates-quantity="availableDatesQuantity"
        :first-available-date="firstAvailableDate"
        :first-available-month="firstAvailableMonth"
        :is-multi-date="isMultiDateFromCalendar"
        @dates-loaded="emit('dates-loaded')"
        @pricings-loaded="emit('pricings-loaded')"
        @add-item-to-cart="emit('add-item-to-cart')"
        @toggle-date-picker="emit('toggle-date-picker')"
        @update:ticket-quantity="emit('update:ticket-quantity', $event)"
      />

      <!-- OR divider above the giftcard card. Special giftcard pages get their
           own divider further down, next to the team-gifting card, so this one
           stays out of their way to avoid rendering two in a row. -->
      <LazyEventBuyBoxOrDivider
        v-if="
          isGiftcardCardShown &&
          !isSpecialGiftcardBuyBoxShown &&
          (shouldDisplayTeamEventRequestCard ||
            shouldDisplayCorporateGiftingCard)
        "
      />

      <!-- Special giftcard pages get the dedicated buy box with delivery options -->
      <LazyEventSpecialGiftcardBuyBox
        v-if="isSpecialGiftcardBuyBoxShown"
        :enable-embedded="isEmbedded"
        :text-color="textColor"
        :background-color="backgroundColor"
        :card-background-color="cardBackgroundColor"
        :type="eventGetters.getIsTeamEvent(eventData) ? 'PRIVATE' : 'PUBLIC'"
        @add-item-to-cart="emit('add-item-to-cart')"
      />

      <LazyEventGiftcardBuyBoxCard
        v-else-if="isGiftcardCardShown"
        :text-color="textColor"
        :enable-embedded="isEmbedded"
        :background-color="backgroundColor"
        :card-background-color="cardBackgroundColor"
        display-price
        :type="eventGetters.getIsTeamEvent(eventData) ? 'PRIVATE' : 'PUBLIC'"
        :should-display-buy-box="shouldDisplayEventBuybox"
        :is-special-giftcard-event="isSpecialGiftcardEvent"
        @add-item-to-cart="emit('add-item-to-cart')"
      />
    </div>

    <LazyTeameventRequestCard
      v-if="shouldDisplayTeamEventRequestCard"
      :price="teamEventRequestPrice"
    />

    <!-- Special giftcard pages use the buy-box styled team-gifting card and
         notify block instead of the plain CorporateGiftingCard -->
    <template v-if="isSpecialGiftcardBuyBoxShown">
      <LazyEventBuyBoxOrDivider />

      <LazyEventBuyBoxRequestCard
        :title="$t('product.show.checkout.corporateGiftRequest.giftcardTitle')"
        :badge="$t('product.show.checkout.corporateGiftRequest.freeBadge')"
        :description="
          $t('product.show.checkout.corporateGiftRequest.giftcardDescription')
        "
      >
        <Button
          variant="outlined"
          class="w-full py-3 flex items-center justify-center gap-2 border-2 font-semibold rounded-xl"
          data-test-id="corporate-gift-request-button"
          link="https://share-eu1.hsforms.com/1ltkibOaNThaZDN40JuR-yAewif8"
          open-new-tab
          @click="sendCorporateGiftRequestGTMEvent('GIFT_CARD')"
        >
          <Gift class="w-4 h-4 stroke-[1.7]" />
          {{ $t('product.show.checkout.corporateGiftRequest.contactUs') }}
        </Button>
      </LazyEventBuyBoxRequestCard>
    </template>

    <LazyCorporateGiftingCard
      v-else-if="shouldDisplayCorporateGiftingCard"
      :event-type="isSpecialGiftcardEvent ? 'GIFT_CARD' : 'BOX'"
    />

    <!-- Waitlist for events that do have dates; the no-dates buy box renders its own -->
    <LazyEventBuyBoxNotifyNewDates v-if="isWaitlistShown" />
  </div>
</template>

<script lang="ts" setup>
import { Gift } from 'lucide-vue-next'
import EventBuyBox from './buybox/EventBuyBox.vue'
import type {
  EventCalendarDayMap,
  EventDescription,
} from '~/composables/types/event'
import { useIsSubdomain } from '~/composables/useSubdomain'
import { eventDateGetters } from '~/utils/eventDateGetters'
import { sendCorporateGiftRequestGTMEvent } from '~/composables/useGtmEvents'

const { locale, t: $t } = useI18n()

const eventBuyBoxVisibleElements = {
  giftcard: true,
  ...(inject('eventBuyBoxVisibleElements') as {
    calendar?: boolean
    requestButtons?: boolean
    giftcard?: boolean
  }),
}

const {
  textColor,
  backgroundColor,
  cardBackgroundColor = 'transparent',
  isEmbedded = false,
} = defineProps<{
  textColor?: string
  backgroundColor?: string
  cardBackgroundColor?: string
  isEmbedded?: boolean
}>()

const emit = defineEmits<{
  (
    e:
      | 'dates-loaded'
      | 'pricings-loaded'
      | 'toggle-date-picker'
      | 'add-item-to-cart'
  ): void
  (e: 'update:ticket-quantity', quantity: number): void
}>()

const eventData = inject<EventDescription | undefined>('eventData')
const isSpecialGiftcardEvent = inject('isSpecialGiftcardEvent') as boolean

// Guard against missing eventData - component will not render without it
if (!eventData && import.meta.dev) {
  console.warn('EventDetailsSidebar: eventData not provided via inject')
}
const router = useRouter()
const isKitsPage = computed(
  () =>
    router.currentRoute.value.name?.toString().split('___')[0] ===
      'k-boxPermalink' ||
    (isEmbedded && eventData && eventGetters.isBoxEvent(eventData))
)

const isSubdomain = useIsSubdomain()
const calendarDates = ref<EventCalendarDayMap | Record<any, any> | undefined>(
  undefined
)

const isBuyBoxReady = ref(isSpecialGiftcardEvent)

const firstAvailableDate = computed(() => {
  if (!calendarDates.value) return undefined
  return eventDateGetters.getFirstAvailableDate(calendarDates.value)
})

const firstAvailableMonth = computed(() => {
  if (!calendarDates.value) return undefined
  return calendarDates.value
})

const areThereDatesAvailable = computed(() =>
  eventDateGetters.areThereDatesAvailable(calendarDates.value)
)

const availableDatesQuantity = computed(
  () => Object.keys(calendarDates.value ?? {}).length || 0
)

const isMultiDateFromCalendar = computed(() => {
  const firstDay = Object.values(calendarDates.value ?? {})[0]
  return eventDateGetters.isMultiDate(firstDay?.dates?.[0])
})

const sessionCountFromCalendar = computed(() => {
  const firstDay = Object.values(calendarDates.value ?? {})[0]
  return eventDateGetters.getSessionsCount(firstDay?.dates?.[0])
})

provide('sessionCountOverride', sessionCountFromCalendar)

const shouldEnableGiftcard = computed(
  () =>
    eventData &&
    eventBuyBoxVisibleElements.giftcard &&
    !eventGetters.getIsTeamEvent(eventData) &&
    (!isSubdomain.value ||
      supplierGetters.getHasGiftcardEmbedded(
        eventGetters.getSupplier(eventData)
      ))
)

const isKitsOutOfStock = computed(
  () =>
    isKitsPage.value &&
    areThereDatesAvailable.value &&
    calendarDates.value?.[0]?.tickets_left === 0
)

const shouldDisplayEventBuybox = computed(() => {
  return (
    !isSpecialGiftcardEvent &&
    areThereDatesAvailable.value &&
    !isKitsOutOfStock.value
  )
})

const hasNoDates = computed(
  () =>
    isBuyBoxReady.value &&
    !isSpecialGiftcardEvent &&
    (!areThereDatesAvailable.value || isKitsOutOfStock.value)
)

const isKitsAvailable = computed(() => {
  return (
    isKitsPage.value &&
    areThereDatesAvailable.value &&
    calendarDates.value?.[0]?.tickets_left > 0
  )
})

// The three cards that can appear in the buy box area. Each computed matches
// the matching component's v-if in the template so they stay in sync.
const isBuyBoxShown = computed(
  () =>
    isBuyBoxReady.value &&
    (shouldDisplayEventBuybox.value || isKitsAvailable.value)
)
const isGiftcardCardShown = computed(
  () => !!shouldEnableGiftcard.value && !hasNoDates.value
)
const isWaitlistShown = computed(
  () => isBuyBoxShown.value && !isEmbedded && !isKitsPage.value
)
const isSpecialGiftcardBuyBoxShown = computed(
  () => isGiftcardCardShown.value && isSpecialGiftcardEvent
)

// Sticky-bar trigger target: the buy box / giftcard area only. Exposed to the
// parent so it can observe visibility and scroll here (see EventDetails.vue).
const buyBoxContainer = useTemplateRef<HTMLElement>('buyBoxContainer')

// The sticky bar has something to point at when any of those cards is shown.
const hasStickyBuyBoxTarget = computed(
  () => hasNoDates.value || isBuyBoxShown.value || isGiftcardCardShown.value
)

defineExpose({ buyBoxContainer, hasStickyBuyBoxTarget })

const shouldDisplayTeamEventRequestCard = computed(
  () =>
    eventData &&
    !isEmbedded &&
    !isSpecialGiftcardEvent &&
    !hasNoDates.value &&
    !eventGetters.isBoxEvent(eventData)
)

const shouldDisplayCorporateGiftingCard = computed(
  () =>
    eventData &&
    !isEmbedded &&
    !isSubdomain.value &&
    (isSpecialGiftcardEvent || eventGetters.isBoxEvent(eventData))
)

const teamEventRequestPrice = computed(() => {
  if (!eventData) return false
  if (eventGetters.getNextDateAvailable(eventData)) return false
  const defaultPriceAmount = eventGetters.getDefaultPriceAmount(eventData)

  if (defaultPriceAmount === 0) return false

  return formatSmartPriceAmount(Number(defaultPriceAmount), locale.value)
})

const config = useRuntimeConfig()
const { apiBaseURL } = config.public

onBeforeMount(async () => {
  await nextTick()
  if (isSpecialGiftcardEvent) {
    isBuyBoxReady.value = true
    return
  }

  // Guard: cannot fetch dates without eventData
  if (!eventData) {
    if (import.meta.dev) {
      console.error(
        'EventDetailsSidebar: Cannot fetch calendar dates without eventData'
      )
    }
    isBuyBoxReady.value = true
    return
  }

  let datesFetched = false
  try {
    calendarDates.value = await $fetch(
      `/v1/store/events/${eventGetters.getId(eventData)}/calendar`,
      {
        method: 'GET',
        baseURL: apiBaseURL,
      }
    )

    datesFetched = true
  } catch (error) {
    console.error('Failed to fetch calendar dates:', error)
    calendarDates.value = undefined
  } finally {
    isBuyBoxReady.value = true
  }

  // Emit only after isBuyBoxReady flips, so listeners measuring the DOM
  // (e.g. embedded iframe height) see the real buy box, not the preloader.
  if (datesFetched) {
    emit('dates-loaded')
  }
})
</script>

<style lang="postcss" scoped>
@import '~/assets/css/loading.css';

.preloader {
  @apply rounded-lg;
}
</style>
